import { Currency } from '@commenda/prisma';
import { IEntitlement } from '@commenda/types';

interface BasePlan {
  id: string;
  name: string;
  price: number;
  quantity: number;
  status: string;
  type: 'COMPLIANCE' | 'MARKETPLACE';
}

interface PlanWithEntitlements extends BasePlan {
  entitlements: (IEntitlement & { entityName: string })[];
}

interface ProcessedPlan extends BasePlan {
  originalProductId: string;
  assignedTo: string;
  category: string;
  itemType: string;
  entitlementId: string;
  entitlementIndex?: number;
  totalEntitlements?: number;
}

export function getNonTimezoneSensitiveDate(date: number): string {
  const timeStampToDate = new Date(date);
  const day = timeStampToDate.getUTCDate();
  const month = timeStampToDate.getUTCMonth();
  const year = timeStampToDate.getUTCFullYear();

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const dateString = `${monthNames[month]} ${day}, ${year}`;
  return dateString;
}

// TODO: (ADARSH) => Think ABout how utils will consume kakashi components
type TBadgeVariant = 'gray' | 'error' | 'warning' | 'success' | 'blue';

export class BillingHelper {
  static INVOICE_STATUS_BADGE_VARIANT_MAP: Record<string, TBadgeVariant> = {
    Complete: 'success',
    Overdue: 'error',
    'Payment Due': 'warning',
  };

  static INVOICE_STATUS_MAP: Record<string, string> = {
    paid: 'Complete',
    overdue: 'Overdue',
    payment_due: 'Payment Due',
  };

  static SUBSCRIPTION_STATUS_MAP: Record<string, string> = {
    active: 'Active',
    cancelled: 'Cancelled',
    paused: 'Paused',
    future: 'Future',
    in_trial: 'In Trial',
    non_renewing: 'Non Renewing',
    transferred: 'Transferred',
  };

  static SUBSCRIPTION_STATUS_BADGE_VARIANT_MAP: Record<string, TBadgeVariant> =
    {
      Active: 'success',
      Cancelled: 'gray',
      Paused: 'warning',
      Future: 'blue',
      'In Trial': 'gray',
      'Non Renewing': 'warning',
      Transferred: 'gray',
    };

  static PLAN_CATEGORY_BADGE_VARIANT_MAP: Record<string, TBadgeVariant> = {
    Compliance: 'gray',
    Marketplace: 'blue',
  };

  static BILLING_FREQUENCY_MAP: Record<string, string> = {
    year: 'Billed yearly',
    month: 'Billed monthly',
    quarter: 'Billed quarterly',
    week: 'Billed weekly',
  };

  static SUPPORTED_BILLING_CURRENCY: Currency[] = [Currency.USD, Currency.INR];

  static getInvoiceStatus(status: string): string {
    return this.INVOICE_STATUS_MAP[status];
  }

  static getInvoiceStatusBadgeVariant(status: string): TBadgeVariant {
    return this.INVOICE_STATUS_BADGE_VARIANT_MAP[status];
  }

  static getSubscriptionStatus(status: string): string {
    return this.SUBSCRIPTION_STATUS_MAP[status] || status;
  }

  static getSubscriptionStatusBadgeVariant(status: string): TBadgeVariant {
    const displayStatus = this.getSubscriptionStatus(status);
    return this.SUBSCRIPTION_STATUS_BADGE_VARIANT_MAP[displayStatus] || 'gray';
  }

  static getPlanCategoryBadgeVariant(category: string): TBadgeVariant {
    return this.PLAN_CATEGORY_BADGE_VARIANT_MAP[category] || 'gray';
  }

  static formatBillingFrequency(frequency: string | null): string {
    if (!frequency) return '';
    const cycle = frequency.toLowerCase();
    return this.BILLING_FREQUENCY_MAP[cycle] || `Billed ${cycle}ly`;
  }

  static getBillingPeriodDisplay(frequency: string | null): string {
    if (!frequency) return '';
    return `/${frequency}`;
  }

  static expandPlanEntitlements(plan: PlanWithEntitlements): ProcessedPlan[] {
    const planWithEntitlements = plan;

    const category =
      planWithEntitlements.type === 'COMPLIANCE' ? 'Compliance' : 'Marketplace';

    const basePrice = plan.price;

    if (!planWithEntitlements.entitlements?.length) {
      return [
        {
          ...plan,
          id: plan.id,
          originalProductId: plan.id,
          assignedTo: '',
          category,
          itemType: 'STANDARD',
          price: basePrice,
          entitlementId: null,
        },
      ];
    }

    return planWithEntitlements.entitlements.map((entitlement, index) => {
      return {
        ...plan,
        id: `${plan.id}-${index}`,
        originalProductId: plan.id,
        assignedTo: entitlement?.entityName || 'Unassigned',
        category,
        itemType: 'STANDARD',
        price: basePrice,
        entitlementId: entitlement?.id || '',
        entitlementIndex: index,
        totalEntitlements: planWithEntitlements.entitlements.length,
      };
    });
  }

  static findMainPlan(plans: BasePlan[]): BasePlan | undefined {
    return (
      plans.find((plan) => plan.type === 'COMPLIANCE') ||
      plans.find((plan) => plan.name.toLowerCase().includes('plan')) ||
      plans[0]
    );
  }
}
